// src/features/stores/storeSlice.js
import { createSlice } from '@reduxjs/toolkit';
import {
  fetchStoresThunk,
  fetchStoreThunk,
  createStoreThunk,
  updateStoreThunk,
  // Incentive-related thunks
  fetchStoreIncentiveStatsThunk,
  updateStoreIncentiveAmountThunk,
  // Store reports thunk
  fetchStoreReportsThunk,
  // Notes thunks
  fetchStoresWithNotesThunk,
  fetchStoreNotesThunk,
  fetchMultipleStoreNotesThunk,
  createStoreNoteThunk,
  updateStoreNoteThunk,
  deleteStoreNoteThunk,
  // Toggle active thunks
  activateStoreThunk,
  deactivateStoreThunk,
} from './storeThunks';

const initialState = {
  stores: [],
  currentStore: null,
  loading: false,
  error: null,
  
  // Store incentive statistics
  incentiveStats: {
    data: null,
    loading: false,
    error: null,
  },
  
  // Store creation response data
  creationResponse: {
    data: null,
    loading: false,
    error: null,
  },
  
  // Store reports summary data
  reportsData: {
    stores: [],
    filters: {},
    totalStores: 0,
    loading: false,
    error: null,
  },
  
  // NEW: Store notes data
  notesData: {
    storesWithNotes: [],
    selectedStoreNotes: null,
    loading: false,
    error: null,
  },
};

const storeSlice = createSlice({
  name: 'stores',
  initialState,
  reducers: {
    clearCurrentStore(state) {
      state.currentStore = null;
    },
    
    // Incentive-related reducers
    clearIncentiveStats(state) {
      state.incentiveStats = { ...initialState.incentiveStats };
    },
    
    resetStoreIncentiveData(state) {
      state.incentiveStats = { ...initialState.incentiveStats };
    },
    
    // Creation response reducers
    clearCreationResponse(state) {
      state.creationResponse = { ...initialState.creationResponse };
    },
    
    resetStoreError(state) {
      state.error = null;
    },
    
    // Store reports reducers
    clearReportsData(state) {
      state.reportsData = { ...initialState.reportsData };
    },
    
    resetReportsError(state) {
      state.reportsData.error = null;
    },
    
    // NEW: Notes reducers
    clearNotesData(state) {
      state.notesData = { ...initialState.notesData };
    },
    
    resetNotesError(state) {
      state.notesData.error = null;
    },
    
    clearSelectedStoreNotes(state) {
      state.notesData.selectedStoreNotes = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Existing store thunks
      .addCase(fetchStoresThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStoresThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.stores = action.payload;
      })
      .addCase(fetchStoresThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // fetchOne
      .addCase(fetchStoreThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.currentStore = null;
      })
      .addCase(fetchStoreThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.currentStore = action.payload;
      })
      .addCase(fetchStoreThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // create
      .addCase(createStoreThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.creationResponse.loading = true;
        state.creationResponse.error = null;
      })
      .addCase(createStoreThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.creationResponse.loading = false;
        
        const responseData = action.payload;
        state.creationResponse.data = responseData;
        
        const storeData = responseData.store || responseData;
        state.stores.push(storeData);
        state.currentStore = storeData;
      })
      .addCase(createStoreThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.creationResponse.loading = false;
        state.creationResponse.error = action.payload;
      })

      // update
      .addCase(updateStoreThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateStoreThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        const idx = state.stores.findIndex((s) => s.id === updated.id);
        if (idx !== -1) {
          state.stores[idx] = updated;
        }
        if (state.currentStore && state.currentStore.id === updated.id) {
          state.currentStore = updated;
        }
      })
      .addCase(updateStoreThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Store Incentive Stats
      .addCase(fetchStoreIncentiveStatsThunk.pending, (state) => {
        state.incentiveStats.loading = true;
        state.incentiveStats.error = null;
      })
      .addCase(fetchStoreIncentiveStatsThunk.fulfilled, (state, action) => {
        state.incentiveStats.loading = false;
        state.incentiveStats.data = action.payload;
      })
      .addCase(fetchStoreIncentiveStatsThunk.rejected, (state, action) => {
        state.incentiveStats.loading = false;
        state.incentiveStats.error = action.payload;
      })

      // Update Store Incentive Amount
      .addCase(updateStoreIncentiveAmountThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateStoreIncentiveAmountThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        
        const idx = state.stores.findIndex((s) => s.id === updated.id);
        if (idx !== -1) {
          state.stores[idx] = { ...state.stores[idx], incentive_amount: updated.incentive_amount };
        }
        
        if (state.currentStore && state.currentStore.id === updated.id) {
          state.currentStore = { ...state.currentStore, incentive_amount: updated.incentive_amount };
        }
      })
      .addCase(updateStoreIncentiveAmountThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Store reports summary thunk handlers
      .addCase(fetchStoreReportsThunk.pending, (state) => {
        state.reportsData.loading = true;
        state.reportsData.error = null;
      })
      .addCase(fetchStoreReportsThunk.fulfilled, (state, action) => {
        state.reportsData.loading = false;
        state.reportsData.stores = action.payload.stores;
        state.reportsData.filters = action.payload.filters;
        state.reportsData.totalStores = action.payload.total_stores;
      })
      .addCase(fetchStoreReportsThunk.rejected, (state, action) => {
        state.reportsData.loading = false;
        state.reportsData.error = action.payload;
      })

      // NEW: Fetch all stores with notes
      .addCase(fetchStoresWithNotesThunk.pending, (state) => {
        state.notesData.loading = true;
        state.notesData.error = null;
      })
      .addCase(fetchStoresWithNotesThunk.fulfilled, (state, action) => {
        state.notesData.loading = false;
        state.notesData.storesWithNotes = action.payload.stores;
      })
      .addCase(fetchStoresWithNotesThunk.rejected, (state, action) => {
        state.notesData.loading = false;
        state.notesData.error = action.payload;
      })
      
      // Fetch store notes
      .addCase(fetchStoreNotesThunk.pending, (state) => {
        state.notesData.loading = true;
        state.notesData.error = null;
      })
      .addCase(fetchStoreNotesThunk.fulfilled, (state, action) => {
        state.notesData.loading = false;
        state.notesData.selectedStoreNotes = action.payload;
      })
      .addCase(fetchStoreNotesThunk.rejected, (state, action) => {
        state.notesData.loading = false;
        state.notesData.error = action.payload;
      })
      
      // Fetch multiple store notes
      .addCase(fetchMultipleStoreNotesThunk.pending, (state) => {
        state.notesData.loading = true;
        state.notesData.error = null;
      })
      .addCase(fetchMultipleStoreNotesThunk.fulfilled, (state, action) => {
        state.notesData.loading = false;
        state.notesData.storesWithNotes = action.payload.stores;
      })
      .addCase(fetchMultipleStoreNotesThunk.rejected, (state, action) => {
        state.notesData.loading = false;
        state.notesData.error = action.payload;
      })
      
      // Create store note
      .addCase(createStoreNoteThunk.pending, (state) => {
        state.notesData.loading = true;
        state.notesData.error = null;
      })
      .addCase(createStoreNoteThunk.fulfilled, (state, action) => {
        state.notesData.loading = false;
        // Add the new note to the selected store notes if it matches
        if (state.notesData.selectedStoreNotes && 
            state.notesData.selectedStoreNotes.store_id === action.payload.store) {
          state.notesData.selectedStoreNotes.notes.unshift(action.payload);
          state.notesData.selectedStoreNotes.notes_count += 1;
          if (action.payload.is_important) {
            state.notesData.selectedStoreNotes.important_notes_count += 1;
          }
        }
      })
      .addCase(createStoreNoteThunk.rejected, (state, action) => {
        state.notesData.loading = false;
        state.notesData.error = action.payload;
      })
      
      // Update store note
      .addCase(updateStoreNoteThunk.pending, (state) => {
        state.notesData.loading = true;
        state.notesData.error = null;
      })
      .addCase(updateStoreNoteThunk.fulfilled, (state, action) => {
        state.notesData.loading = false;
        // Update the note in the selected store notes
        if (state.notesData.selectedStoreNotes) {
          const noteIndex = state.notesData.selectedStoreNotes.notes.findIndex(
            note => note.id === action.payload.id
          );
          if (noteIndex !== -1) {
            const oldNote = state.notesData.selectedStoreNotes.notes[noteIndex];
            state.notesData.selectedStoreNotes.notes[noteIndex] = action.payload;
            
            // Update important count if importance changed
            if (oldNote.is_important && !action.payload.is_important) {
              state.notesData.selectedStoreNotes.important_notes_count -= 1;
            } else if (!oldNote.is_important && action.payload.is_important) {
              state.notesData.selectedStoreNotes.important_notes_count += 1;
            }
          }
        }
      })
      .addCase(updateStoreNoteThunk.rejected, (state, action) => {
        state.notesData.loading = false;
        state.notesData.error = action.payload;
      })
      
      // Delete store note
      .addCase(deleteStoreNoteThunk.pending, (state) => {
        state.notesData.loading = true;
        state.notesData.error = null;
      })
      .addCase(deleteStoreNoteThunk.fulfilled, (state, action) => {
        state.notesData.loading = false;
        // Remove the note from the selected store notes
        if (state.notesData.selectedStoreNotes) {
          const noteIndex = state.notesData.selectedStoreNotes.notes.findIndex(
            note => note.id === action.payload
          );
          if (noteIndex !== -1) {
            const deletedNote = state.notesData.selectedStoreNotes.notes[noteIndex];
            state.notesData.selectedStoreNotes.notes.splice(noteIndex, 1);
            state.notesData.selectedStoreNotes.notes_count -= 1;
            if (deletedNote.is_important) {
              state.notesData.selectedStoreNotes.important_notes_count -= 1;
            }
          }
        }
      })
      .addCase(deleteStoreNoteThunk.rejected, (state, action) => {
        state.notesData.loading = false;
        state.notesData.error = action.payload;
      })

      // Deactivate store
      .addCase(deactivateStoreThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deactivateStoreThunk.fulfilled, (state, action) => {
        state.loading = false;
        const storeId = action.payload;
        const idx = state.stores.findIndex((s) => s.id === storeId);
        if (idx !== -1) {
          state.stores[idx] = { ...state.stores[idx], is_active: false };
        }
        if (state.currentStore && state.currentStore.id === storeId) {
          state.currentStore = { ...state.currentStore, is_active: false };
        }
      })
      .addCase(deactivateStoreThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Activate store
      .addCase(activateStoreThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(activateStoreThunk.fulfilled, (state, action) => {
        state.loading = false;
        const storeId = action.payload;
        const idx = state.stores.findIndex((s) => s.id === storeId);
        if (idx !== -1) {
          state.stores[idx] = { ...state.stores[idx], is_active: true };
        }
        if (state.currentStore && state.currentStore.id === storeId) {
          state.currentStore = { ...state.currentStore, is_active: true };
        }
      })
      .addCase(activateStoreThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearCurrentStore,
  clearIncentiveStats,
  resetStoreIncentiveData,
  clearCreationResponse,
  resetStoreError,
  clearReportsData,
  resetReportsError,
  clearNotesData,
  resetNotesError,
  clearSelectedStoreNotes
} = storeSlice.actions;

export default storeSlice.reducer;